import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createVNode as _createVNode, withCtx as _withCtx, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "links" }
const _hoisted_4 = { class: "button-grid" }

import { computed, ref, useTemplateRef, defineAsyncComponent } from 'vue';
import { useClipboard } from '@vueuse/core';

import axios from '@nextcloud/axios';
import { showError, showSuccess } from '@services/utils/dialog';

import NcButton from '@nextcloud/vue/components/NcButton';
import NcActionButton from '@nextcloud/vue/components/NcActionButton';
import Modal from '@components/modal/Modal.vue';
import XLoadingIcon from '@components/XLoadingIcon.vue';

import { useModal } from '@services/modal';
import { t } from '@services/l10n';
import { windowDims } from '@services/viewport';
import { API } from '@services/API';
import { isAbortError, useAbort } from '@services/utils/abort-vue';
import * as utils from '@services/utils/common';
import * as nativex from '@native';

import type { IShare } from '@typings';

import CloseIcon from 'vue-material-design-icons/Close.vue';
import LinkIcon from 'vue-material-design-icons/LinkVariant.vue';


export default /*@__PURE__*/_defineComponent({
  ...{
  name: 'NodeShareModal',
},
  __name: 'NodeShareModal',
  setup(__props) {

const NcListItem = defineAsyncComponent(() => import('@nextcloud/vue/components/NcListItem'));



const modal = useTemplateRef('modal');
const { show } = useModal(modal);

const filename = ref('');
const loading = ref(0);
const shares = ref<IShare[]>([]);

const abort = useAbort();
const { copy: copyText } = useClipboard({ legacy: true });

const isRoot = computed(() => filename.value === '/' || filename.value === '');
const sidebar = computed(() => (!isRoot.value && !windowDims.isMobile ? filename.value : null));

console.assert(!_m.modals.shareNodeLink, 'NodeShareModal created twice');
_m.modals.shareNodeLink = open;

async function open(path: string, immediate?: boolean) {
  filename.value = path;
  show.value = true;
  shares.value = [];
  _m.sidebar.setTab('sharing');

  // Get current shares
  await refreshUrls();

  // Immediate sharing
  // If an existing share is found, just share it directly if it's
  // not password protected. Otherwise create a new share.
  if (immediate) {
    // create a new share if none exists
    if (shares.value.length === 0) {
      await createLink();
    } else {
      // find share with no password
      const share = shares.value.find((s) => !s.hasPassword);
      if (share) shareOrCopy(share.url);
    }
  }
}

async function shareOrCopy(url: string) {
  if (nativex.has()) {
    return await nativex.shareUrl(url);
  }

  await copy(url);

  try {
    await window.navigator?.share?.({ title: filename.value, url: url });
  } catch (e) {
    if (isAbortError(e)) return;
    console.error(e);
  }
}

function cleanup() {
  abort.abort();
  show.value = false;
}

async function refreshUrls() {
  const signal = abort.renew();
  loading.value++;
  try {
    shares.value = (await axios.get(API.Q(API.SHARE_LINKS(), { path: filename.value }), { signal })).data;
    signal.throwIfAborted();
  } catch (e) {
    if (isAbortError(e)) return;
    shares.value = [];
  } finally {
    loading.value--;
  }
}

function getShareLabels(share: IShare): string {
  const labels: string[] = [];
  if (share.hasPassword) {
    labels.push(t('memories', 'Password protected'));
  }

  if (share.expiration) {
    const exp = utils.getLongDateStr(new Date(share.expiration * 1000));
    const kw = t('memories', 'Expires');
    labels.push(`${kw} ${exp}`);
  }

  if (share.editable) {
    labels.push(t('memories', 'Editable'));
  }

  if (labels.length > 0) {
    return `${labels.join(', ')}`;
  }

  return t('memories', 'Read only');
}

async function createLink(): Promise<IShare | null> {
  const signal = abort.renew();
  loading.value++;
  try {
    const res = await axios.post<IShare>(API.SHARE_NODE(), { path: filename.value }, { signal });
    const newShare = res.data;
    signal.throwIfAborted();
    shares.value.push(newShare);
    refreshSidebar();
    shareOrCopy(newShare.url);
    return newShare;
  } catch (e) {
    if (isAbortError(e)) return null;
    console.error(e);
    showError(t('memories', 'Failed to create share link'));
    return null;
  } finally {
    loading.value--;
  }
}

async function deleteLink(share: IShare) {
  const signal = abort.renew();
  loading.value++;
  try {
    await axios.post(API.SHARE_DELETE(), { id: share.id }, { signal });
    signal.throwIfAborted();
  } catch (e) {
    if (isAbortError(e)) return;
    console.error(e);
    showError(t('memories', 'Failed to delete share link'));
    return;
  } finally {
    loading.value--;
  }
  refreshUrls();
  refreshSidebar();
}

async function copy(url: string) {
  try {
    await copyText(url);
    showSuccess(t('memories', 'Link copied to clipboard'));
  } catch (e) {
    if (isAbortError(e)) return;
    showError(t('memories', 'Failed to copy link to clipboard'));
  }
}

function refreshSidebar() {
  if (windowDims.isMobile) return;
  _m.sidebar.close();
  _m.sidebar.open(0, filename.value, true);
}

return (_ctx: any,_cache: any) => {
  return (_unref(show))
    ? (_openBlock(), _createBlock(Modal, {
        key: 0,
        ref_key: "modal",
        ref: modal,
        onClose: cleanup,
        size: "normal",
        sidebar: sidebar.value
      }, {
        title: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('memories', 'Link Sharing')), 1)
        ]),
        buttons: _withCtx(() => [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_unref(NcButton), {
              class: "primary",
              disabled: !!loading.value,
              onClick: createLink
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('memories', 'Create Link')), 1)
              ]),
              _: 1
            }, 8, ["disabled"]),
            _createVNode(_unref(NcButton), {
              class: "primary",
              disabled: !!loading.value,
              onClick: refreshUrls
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('memories', 'Refresh')), 1)
              ]),
              _: 1
            }, 8, ["disabled"])
          ])
        ]),
        default: _withCtx(() => [
          (isRoot.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, _toDisplayString(_unref(t)('memories', 'You cannot share the root folder')), 1))
            : (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createTextVNode(_toDisplayString(_unref(t)('memories', 'Public link shares are available to people outside Nextcloud.')) + " ", 1),
                _cache[0] || (_cache[0] = _createElementVNode("br", null, null, -1)),
                _createTextVNode(" " + _toDisplayString(_unref(t)('memories', 'You may create or update permissions on public links using the sidebar.')) + " ", 1),
                _cache[1] || (_cache[1] = _createElementVNode("br", null, null, -1)),
                _createTextVNode(" " + _toDisplayString(_unref(t)('memories', 'Click a link to copy to clipboard.')), 1)
              ])),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("ul", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(shares.value, (share) => {
                return (_openBlock(), _createBlock(_unref(NcListItem), {
                  name: share.label || _unref(t)('memories', 'Share link'),
                  key: share.id,
                  bold: false,
                  href: share.url,
                  compact: true,
                  onClick: _withModifiers(($event: any) => (shareOrCopy(share.url)), ["prevent"])
                }, {
                  icon: _withCtx(() => [
                    _createVNode(LinkIcon, {
                      class: "avatar",
                      size: 20
                    })
                  ]),
                  subname: _withCtx(() => [
                    _createTextVNode(_toDisplayString(getShareLabels(share)), 1)
                  ]),
                  actions: _withCtx(() => [
                    _createVNode(_unref(NcActionButton), {
                      onClick: ($event: any) => (deleteLink(share)),
                      disabled: !!loading.value
                    }, {
                      icon: _withCtx(() => [
                        _createVNode(CloseIcon, { size: 20 })
                      ]),
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)('memories', 'Remove')) + " ", 1)
                      ]),
                      _: 1
                    }, 8, ["onClick", "disabled"])
                  ]),
                  _: 2
                }, 1032, ["name", "href", "onClick"]))
              }), 128))
            ])
          ]),
          (loading.value)
            ? (_openBlock(), _createBlock(XLoadingIcon, { key: 2 }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["sidebar"]))
    : _createCommentVNode("", true)
}
}

})